import {
  IAppState,
  ICaptain,
  IMember,
  IMembersListData,
} from '../../types/state.d';
import React, { useEffect, useState } from 'react';
import {
  getCaptain,
  getIsCaptainInMembersList,
  getOccasion,
  getPageInfo,
  getSortedMembersList,
} from '../../selectors/apiSelectors';
import {
  getRequestError,
  getRequestInFlight,
} from '../../selectors/httpSelectors';

import Alert from '@justgiving/alert';
import { Button } from '@justgiving/react-tracking';
import CaptainCard from '../CaptainCard/CaptainCard';
import Loading from '@justgiving/loading';
import MemberCard from './MemberCard';
import { Occasion, OccasionType, PageInfo } from '../../../types/graphql';
import { REQUEST } from '../../consts';
import { Trans } from 'react-i18next';
import UnlinkMemberModal from '../UnlinkMemberModal/UnlinkMemberModal';
import classnames from 'classnames';
import { connect } from 'react-redux';
import fetchMembersList from '../../actions/thunks/fetchMembersList';
import { resetRequestError } from '../../actions/actionCreators/httpActions';
import unlinkMember from '../../actions/thunks/unlinkMember';
import { useTracking } from '@justgiving/react-tracking';

type Props = MapDispatchToProps & MapStateToProps;

export const MemberCardList = ({
  isCaptainInMembersList,
  sortedMembersList,
  captain,
  isInFlight,
  isError,
  isUnlinkingError,
  pageInfo,
  occasion,
  ...props
}: Props) => {
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [memberToBeUnlinked, setMemberToBeUnlinked] = useState<
    IMember | undefined
  >(undefined);

  const track = useTracking();

  useEffect(() => {
    props.fetchMembersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOpenUnlinkModal = (member: IMember) => {
    setMemberToBeUnlinked(member);
    setIsUnlinkModalOpen(true);
    track({
      action_name: 'click',
      subtype: 'team member',
      event: 'click',
      event_value: 'unlink',
      page_section: 'admin > members',
    });
  };

  const onCloseUnlinkModal = async () => {
    setIsUnlinkModalOpen(false);
    window.location.reload();
  };

  const ulClasses: string = classnames({
    'jg-tc-ul-unstyled': true,
    'jg-space-mbn': true,
    'jg-space-pbxl': !pageInfo?.hasNextPage,
    'jg-space-pblg': pageInfo?.hasNextPage,
  });

  if (isInFlight) {
    return (
      <>
        <Loading size="small" />
        <span className="jg-sr-only" aria-live="polite">
          <Trans i18nKey="content.editteam.loading.sronly" />
        </span>
      </>
    );
  }

  return (
    <>
      <ul className={ulClasses}>
        {!isCaptainInMembersList && (
          <CaptainCard captain={captain} isInMemory={occasion?.type === OccasionType.InMemory} />
        )}
        {sortedMembersList?.map((member: IMember) => {
          const isCaptain = captain.userGuid === member.userGuid;
          return (
            <MemberCard
              key={member.fundraisingPageGuid}
              isCaptain={isCaptain}
              onClick={() => onOpenUnlinkModal(member)}
              member={member}
              canUnlinkMember={sortedMembersList.length > 1}
            />
          );
        })}
      </ul>

      {pageInfo?.hasNextPage && (
        <div className="jg-tc-button-container">
          <Button
            id="jg-tc-fetch-more-members"
            data-qa="jg-tc-fetch-more-members"
            onClick={() => props.fetchMembersList()}
            className="jg-btn jg-btn--ghost jg-space-mbmd"
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'fetch more',
              page_section: 'page creation > fetch more members',
            }}
          >
            {isInFlight ? (
              <Loading size="small" colour="white" />
            ) : (
              <Trans i18nKey="membersList.loadMore" />
            )}
          </Button>
        </div>
      )}

      {memberToBeUnlinked && (
        <UnlinkMemberModal
          onCloseModal={onCloseUnlinkModal}
          isModalOpen={isUnlinkModalOpen}
          unlinkMember={props.unlinkMember}
          member={memberToBeUnlinked}
          isInFlight={isInFlight}
          isError={isUnlinkingError}
        />
      )}
      {isError && (
        <Alert type="error" displayType="inline" onClose={resetRequestError}>
          <Trans i18nKey="global.error.fatal" />
        </Alert>
      )}
    </>
  );
};

type MapStateToProps = {
  sortedMembersList: IMembersListData['members'];
  isInFlight?: boolean;
  isError: boolean;
  isUnlinkingError: boolean;
  isCaptainInMembersList: boolean;
  captain: ICaptain;
  pageInfo?: PageInfo;
  occasion?: Occasion;
};
type MapDispatchToProps = {
  resetRequestError: typeof resetRequestError;
  fetchMembersList: typeof fetchMembersList;
  unlinkMember: typeof unlinkMember;
};

const mapStateToProps = (state: IAppState): MapStateToProps => ({
  sortedMembersList: getSortedMembersList(state),
  isInFlight: getRequestInFlight(REQUEST.GET_MEMBERS_LIST)(state),
  isError: getRequestError(REQUEST.GET_MEMBERS_LIST)(state),
  isUnlinkingError: getRequestError(REQUEST.UNLINK_MEMBER)(state),
  isCaptainInMembersList: getIsCaptainInMembersList(state) ?? false,
  captain: getCaptain(state),
  pageInfo: getPageInfo(state),
  occasion: getOccasion(state),
});

const mapDispatchToProps: MapDispatchToProps = {
  resetRequestError,
  fetchMembersList,
  unlinkMember,
};
export default connect(mapStateToProps, mapDispatchToProps)(MemberCardList);
